import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Download, X, Share, Plus, MoreVertical, Smartphone, Monitor } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

/**
 * One-time PWA install nudge that appears at the top of the dashboard
 * starting from the user's SECOND successful login on a mobile/tablet
 * device. Dismissable permanently per-user via localStorage.
 *
 * - Android / Chrome: triggers the native `beforeinstallprompt` flow.
 * - iOS Safari: shows a Share → Add to Home Screen instructions modal
 *   (Apple does not expose a programmatic install API).
 * - Desktop: shows browser-address-bar install instructions.
 * - Hidden on already-installed standalone mode or when the user
 *   previously dismissed it.
 */
const DISMISS_KEY_PREFIX = 'sc365_pwa_install_dismissed_';
const LOGIN_COUNT_KEY_PREFIX = 'sc365_login_count_';
const SHOW_AFTER_LOGIN_COUNT = 2;

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  // iOS uses navigator.standalone, others use display-mode media query.
  const mq = window.matchMedia('(display-mode: standalone)').matches;
  const ios = typeof window.navigator !== 'undefined' && window.navigator.standalone === true;
  return mq || ios;
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  // iPadOS 13+ reports as Mac with touch
  if (/macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

export default function PWAInstallBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [platform] = useState(detectPlatform);

  const userId = user?.id;
  const dismissKey = userId ? `${DISMISS_KEY_PREFIX}${userId}` : null;
  const loginCountKey = userId ? `${LOGIN_COUNT_KEY_PREFIX}${userId}` : null;

  // Decide whether to show the banner. Re-runs when user changes.
  const evaluate = useCallback(() => {
    if (!userId || !dismissKey || !loginCountKey) return false;
    if (!isMobileViewport()) return false;
    if (isStandalone()) return false;
    if (localStorage.getItem(dismissKey) === '1') return false;
    const count = Number(localStorage.getItem(loginCountKey) || 0);
    return count >= SHOW_AFTER_LOGIN_COUNT;
  }, [userId, dismissKey, loginCountKey]);

  useEffect(() => {
    setVisible(evaluate());
  }, [evaluate]);

  // Capture the Android/Chrome install prompt so we can fire it later.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const onInstalled = () => {
      // Hide immediately after the install completes.
      setVisible(false);
      setDeferredPrompt(null);
      if (dismissKey) localStorage.setItem(dismissKey, '1');
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [dismissKey]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'accepted') {
          setVisible(false);
          if (dismissKey) localStorage.setItem(dismissKey, '1');
        }
      } catch {
        // user cancelled — keep banner so they can try again later
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    // No native prompt — show platform-specific instructions modal
    setInstructionsOpen(true);
  };

  const handleDismiss = () => {
    setVisible(false);
    if (dismissKey) localStorage.setItem(dismissKey, '1');
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="lg:hidden mx-4 mt-4 rounded-xl border border-[#4338CA]/20 bg-gradient-to-r from-indigo-50 to-white p-3 shadow-sm"
        data-testid="pwa-install-banner"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#4338CA] flex items-center justify-center shrink-0">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 leading-tight">
              Add ShiftCover365 to your home screen
            </p>
            <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
              {platform === 'ios' && 'Tap Share → Add to Home Screen in Safari.'}
              {platform === 'android' && 'Tap Menu → Install app in Chrome.'}
              {platform === 'desktop' && 'Look for the Install icon in your address bar.'}
            </p>
            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                className="h-11 rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white text-[12px] px-4"
                onClick={handleInstall}
                data-testid="pwa-install-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-11 rounded-full text-slate-500 hover:text-slate-700 text-[12px] px-3"
                onClick={handleDismiss}
                data-testid="pwa-dismiss-btn"
              >
                Don&apos;t show again
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-600 p-1 -mt-1 -mr-1 shrink-0"
            aria-label="Dismiss install banner"
            data-testid="pwa-close-x-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Platform-aware install instructions modal */}
      <Dialog open={instructionsOpen} onOpenChange={setInstructionsOpen}>
        <DialogContent className="sm:max-w-sm" data-testid={`pwa-${platform}-instructions-modal`}>
          <DialogHeader>
            <DialogTitle>
              {platform === 'ios' && 'Install on your iPhone'}
              {platform === 'android' && 'Install on Android'}
              {platform === 'desktop' && 'Install on your desktop'}
            </DialogTitle>
            <DialogDescription>
              {platform === 'ios' && 'Two quick steps in Safari and you\'re done.'}
              {platform === 'android' && 'Quick steps in Chrome and you\'re done.'}
              {platform === 'desktop' && 'Look in your browser address bar.'}
            </DialogDescription>
          </DialogHeader>

          {platform === 'ios' && (
            <ol className="space-y-3 text-sm text-slate-700" data-testid="pwa-ios-steps">
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>
                  Tap the <Share className="inline h-4 w-4 mx-0.5 -mt-0.5 text-[#4338CA]" /> <strong>Share</strong> button at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>
                  Scroll down and tap <Plus className="inline h-4 w-4 mx-0.5 -mt-0.5 text-[#4338CA]" /> <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Tap <strong>Add</strong> in the top-right.</span>
              </li>
            </ol>
          )}

          {platform === 'android' && (
            <ol className="space-y-3 text-sm text-slate-700" data-testid="pwa-android-steps">
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>
                  Tap the <MoreVertical className="inline h-4 w-4 mx-0.5 -mt-0.5 text-[#4338CA]" /> <strong>Menu</strong> icon in your browser.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>
                  Tap <strong>Install app</strong> or <strong>Add to Home Screen</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Tap <strong>Install</strong> to confirm.</span>
              </li>
            </ol>
          )}

          {platform === 'desktop' && (
            <ol className="space-y-3 text-sm text-slate-700" data-testid="pwa-desktop-steps">
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>
                  Look for the <Monitor className="inline h-4 w-4 mx-0.5 -mt-0.5 text-[#4338CA]" /> <strong>Install</strong> icon at the right of your browser&apos;s address bar.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Click <strong>Install</strong> in the prompt that appears.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-[#4338CA] text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>ShiftCover365 will open in its own window like a native app.</span>
              </li>
            </ol>
          )}

          <p className="text-xs text-slate-500 leading-relaxed">
            Once installed, open ShiftCover365 from your home screen or apps list &mdash; it&apos;ll launch full-screen, just like a native app.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-full h-11"
              onClick={() => setInstructionsOpen(false)}
              data-testid="pwa-instructions-close-btn"
            >
              Got it
            </Button>
            <Button
              className="rounded-full h-11 bg-[#4338CA] hover:bg-[#3730A3] text-white"
              onClick={() => {
                setInstructionsOpen(false);
                handleDismiss();
              }}
              data-testid="pwa-instructions-dismiss-permanent-btn"
            >
              Don&apos;t show again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
